import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../utils/api';

/**
 * Public, no-auth landing page for an event recap share link.
 *
 * URL: /share/event-recap/:token
 *
 * Strategy:
 *   The backend exposes /api/share/event-recap/:token which streams the
 *   MP4 directly. This page wraps that endpoint in a friendly branded
 *   <video> player so parents see context (event title, org name) and
 *   not a bare MP4. We make a HEAD-style ping first to detect 404/410
 *   and show appropriate empty/expired states.
 *
 * Accessibility:
 *   - Uses semantic <main>, <h1> for screen readers
 *   - Video element gets `controls` + `playsInline` so iOS Safari plays
 *     inline instead of forcing fullscreen
 */
function RecapShare() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | notfound | expired | error
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function probe() {
      if (!token) {
        setStatus('notfound');
        return;
      }
      const url = api.buildRecapStreamUrl(token);
      try {
        // Probe with a Range request for 0-0 — cheap way to learn the
        // HTTP status without downloading the whole video.
        const res = await fetch(url, { method: 'GET', headers: { Range: 'bytes=0-0' } });
        if (cancelled) return;
        if (res.status === 200 || res.status === 206) {
          setVideoUrl(url);
          setStatus('ready');
        } else if (res.status === 410) {
          setStatus('expired');
        } else if (res.status === 404) {
          setStatus('notfound');
        } else {
          setStatus('error');
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    }
    probe();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <main
      data-testid="recap-share-page"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, hsl(189 53% 25%) 0%, hsl(189 60% 12%) 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '3rem 1rem',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: '0.78rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#a0d8d3',
            marginBottom: '0.5rem',
          }}
        >
          Kids WiseApp
        </div>
        <h1 style={{ fontSize: '1.7rem', margin: '0 0 0.6rem 0', fontWeight: 700, letterSpacing: '-0.01em' }}>
          🎬 Event recap video
        </h1>
        <p style={{ color: '#cfeeeb', fontSize: '0.92rem', marginTop: 0, marginBottom: '1.5rem' }}>
          Here's a quick recap your child's setting put together. Enjoy!
        </p>

        {status === 'loading' && (
          <div
            data-testid="recap-share-loading"
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: '3rem 1rem',
              color: '#cfeeeb',
            }}
          >
            Loading recap…
          </div>
        )}

        {status === 'ready' && videoUrl && (
          <div
            data-testid="recap-share-player"
            style={{
              background: '#000',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 30px 60px rgba(0,0,0,0.35)',
            }}
          >
            <video
              src={videoUrl}
              controls
              autoPlay
              playsInline
              preload="metadata"
              style={{ width: '100%', display: 'block' }}
              data-testid="recap-share-video"
            />
          </div>
        )}

        {status === 'ready' && videoUrl && (
          <div style={{ marginTop: '1rem' }}>
            <a
              href={videoUrl}
              download
              data-testid="recap-share-download"
              style={{
                display: 'inline-block',
                padding: '0.55rem 1.1rem',
                background: 'rgba(255,255,255,0.12)',
                color: '#fff',
                borderRadius: 999,
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.88rem',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              ⬇ Download video
            </a>
          </div>
        )}

        {status === 'expired' && (
          <div
            data-testid="recap-share-expired"
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: '2rem 1rem',
              color: '#fbe2c1',
              fontSize: '0.95rem',
            }}
          >
            ⌛ This share link has expired. Please ask your child's setting for an updated link.
          </div>
        )}

        {status === 'notfound' && (
          <div
            data-testid="recap-share-notfound"
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: '2rem 1rem',
              color: '#fbe2c1',
              fontSize: '0.95rem',
            }}
          >
            🤷 We couldn't find this recap. The link may have been removed or rotated by the setting.
          </div>
        )}

        {status === 'error' && (
          <div
            data-testid="recap-share-error"
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: '2rem 1rem',
              color: '#fbb',
              fontSize: '0.95rem',
            }}
          >
            ⚠️ Something went wrong loading the recap. Please try again later.
          </div>
        )}

        <p style={{ marginTop: '2rem', fontSize: '0.74rem', color: '#9ec9c6' }}>
          Powered by Kids WiseApp · Childcare ERP
        </p>
      </div>
    </main>
  );
}

export default RecapShare;
